import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { companyInfo } from '../data/mock';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from './ui/navigation-menu';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', href: '/' },
    { name: 'Design Ideas', href: '/design-ideas' },
    { name: 'About Us', href: '/about' },
    {
      name: 'Services',
      href: '/services',
      dropdown: [
        { name: 'Turnkey Interiors', href: '/services/turnkey-interiors' },
        { name: 'Bedroom Interiors', href: '/services/bedroom-interiors' },
        { name: 'Modular Kitchen', href: '/services/modular-kitchen' },
        { name: 'Customized Kitchen', href: '/services/customized-kitchen' },
      ],
    },
    { name: 'Packages', href: '/packages' },
    { name: 'Contact Us', href: '/contact' },
    { name: 'Construction', href: '/construction' },
    { name: 'Blog', href: '/blog' },
  ];

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg py-3'
          : 'bg-white py-4'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src={companyInfo.logo}
              alt={companyInfo.name}
              className="h-10 md:h-12 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center gap-1">
            {navLinks.map((link) => (
              link.dropdown ? (
                <div key={link.name} className="relative group">
                  <button
                    className={`flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors duration-200 rounded-lg hover:bg-brand-light ${
                      isActive(link.href) ? 'text-brand-primary' : 'text-gray-700'
                    }`}
                  >
                    {link.name}
                    <ChevronDown className="w-4 h-4 transition-transform group-hover:rotate-180" />
                  </button>
                  <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-xl shadow-xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="py-2">
                      {link.dropdown.map((item) => (
                        <Link
                          key={item.name}
                          to={item.href}
                          className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-brand-light hover:text-brand-primary transition-colors"
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`px-3 py-2 text-sm font-medium transition-colors duration-200 rounded-lg hover:bg-brand-light ${
                    isActive(link.href) ? 'text-brand-primary bg-brand-light/50' : 'text-gray-700'
                  }`}
                >
                  {link.name}
                </Link>
              )
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden xl:flex items-center gap-3">
            <a
              href={`tel:${companyInfo.phone}`}
              className="flex items-center gap-2 text-brand-primary font-medium text-sm"
            >
              <Phone className="w-4 h-4" />
              <span>{companyInfo.phone}</span>
            </a>
            <Link to="/contact">
              <Button className="bg-brand-primary hover:bg-brand-dark text-white px-5 py-2 text-sm">
                Get Estimate
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="xl:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-gray-700" />
            ) : (
              <Menu className="w-6 h-6 text-gray-700" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          className={`xl:hidden overflow-hidden transition-all duration-300 ${
            isMobileMenuOpen ? 'max-h-[600px] mt-4' : 'max-h-0'
          }`}
        >
          <nav className="flex flex-col gap-1 bg-white rounded-xl p-4 shadow-lg border border-gray-100">
            {navLinks.map((link) => (
              link.dropdown ? (
                <div key={link.name}>
                  <button
                    onClick={() => setIsServicesOpen(!isServicesOpen)}
                    className="flex items-center justify-between w-full text-gray-700 font-medium py-2 px-4 rounded-lg hover:bg-brand-light hover:text-brand-primary transition-colors"
                  >
                    {link.name}
                    <ChevronDown className={`w-4 h-4 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} />
                  </button>
                  <div className={`overflow-hidden transition-all duration-200 ${isServicesOpen ? 'max-h-48' : 'max-h-0'}`}>
                    {link.dropdown.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className="block text-gray-600 py-2 px-8 rounded-lg hover:bg-brand-light hover:text-brand-primary transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`text-gray-700 font-medium py-2 px-4 rounded-lg hover:bg-brand-light hover:text-brand-primary transition-colors ${
                    isActive(link.href) ? 'bg-brand-light text-brand-primary' : ''
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.name}
                </Link>
              )
            ))}
            <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
              <Button className="bg-brand-primary hover:bg-brand-dark text-white mt-2 w-full">
                Get Estimate
              </Button>
            </Link>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
